<template>
  <div v-if="!uiStore.isGlobalFullscreen" ref="widget" class="widget" :style="getStyle()">
    <div class="btn-wrap px-2 move" data-html2canvas-ignore
      @mousedown.self="$emit('move', { target: widget, $event, name })">
      <div class="radio-btn-toggle">
        <template v-if="selectChartType.isShow">
          <template v-for="(type, index) in selectChartType.options" :key="`${type}${name}`">
            <input v-model="localSelectedChartType" :value="type" :id="`${type}${name}`" type="radio" class="btn-check">
            <label :for="`${type}${name}`" class="btn btn-sm px-2">
              <i :class="[
                'fa-solid',
                `fa-chart-${{ bar: 'column', line: 'line', doughnut: 'pie', vertical: 'simple', horizontal: 'simple' }[type]}`,
                type === 'horizontal' ? 'horizontal' : ''
              ]"></i>
            </label>
          </template>
        </template>
      </div>
      <button v-if="!navigatorStore.isMobile" type="button" class="btn btn-sm px-2 ml-auto" @click="displayFullscreen">
        <i class="fa-solid fa-expand"></i>
      </button>
      <button v-if="!navigatorStore.isMobile"  type="button" :class="[!navigatorStore.isMobile? 'btn btn-sm px-2':'btn btn-sm px-2 ml-auto']" @click="captureWidget">
        <i class="fa-solid fa-envelope-open-text"></i>
      </button>
    </div>
    <h4 class="title">{{ name }}</h4>

    <div class="options">
      <ElSelect v-if="displayFiscalYear" v-model="localSelectedFiscalYear" :options="fiscalYearOptions" />
      <ElSelect v-if="shouldDisplayEmployeeIdGroup1(name, userStore.isAdmin)" v-model="localSelectedEmployeeId" :options="employeeIdOptions" />
      <template v-if="selectLegend.isShow">
        <div v-if="!navigatorStore.isMobile && selectLegend.options.length < 4" class="radio-btn-toggle">
          <ElLabel v-for="item in selectLegend.options" :name="item.text" classNm="btn btn-sm">
            <template #start="{ id }">
              <ElRadio v-model="localSelectedLegend" :value="item.value" class="btn-check" :id="id"
                :name="`${name}-legend`" />
            </template>
          </ElLabel>
        </div>
        <ElSelect v-else v-model="localSelectedLegend" :value="localSelectedLegend" :options="selectLegend.options" />
      </template>
    </div>
    <div class="options2">
      <ElSelect v-if="shouldDisplayEmployeeIdGroup2(name, userStore.isAdmin)" v-model="localSelectedEmployeeId" :options="employeeIdOptions" /></div>
    <component ref="chart" :is='getChartComponent' :gridArea="gridArea" :employeeId="localSelectedEmployeeId"
      :legend="localSelectedLegend" :chartType="localSelectedChartType" :width="clientRect.width" :fiscalYear="localSelectedFiscalYear"
      :height="clientRect.height - (displayEmployeeId ? 180 : 150)" :title="name" />
    <div class="widget-bottom" data-html2canvas-ignore>
      <router-link draggable="false" v-if="linkReport" :to="linkReport" :class="shouldDisplayEmployeeIdGroup2(name, userStore.isAdmin) && shouldDisplayEmployeeIdGroup1(name, userStore.isAdmin) ?'link-report2' :'link-report'">Link Report</router-link>
    </div>
    <button data-html2canvas-ignore class="btn btn-resize"
      @mousedown.self="$emit('resize', { target: widget, $event, name })">
    </button>
  </div>

</template>

<script setup>
// 필요한 Vue 기능 및 컴포넌트 가져오기
import { ref, computed, onMounted, watch, inject, reactive, onBeforeMount, nextTick } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import FCSTSummary from './chart/FCSTSummary.vue'
import PipelineManagement from './chart/PipelineManagement.vue'
import FinalBalanceFCST from './chart/FinalBalanceFCST.vue'
import PipelineHygiene from './chart/PipelineHygiene.vue'
import TotalRevFCST from './chart/TotalRevFCST.vue'
import TotalGMFCST from './chart/TotalGMFCST.vue'
import RevFCST from './chart/RevFCST.vue'
import GMFCST from './chart/GMFCST.vue'
import MonthlyRevGMFCST from './chart/MonthlyRevGMFCST.vue'
import PipelineStatus from './chart/PipelineStatus.vue'
import WeeklyHistory from './chart/WeeklyHistory.vue'
import common from "@/js/common";
import { useUiStore } from '@/store/ui' // UI 스토어 임포트

import Swiper from 'swiper'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
//import 'swiper/css/pagination'  // 사용 안 하는 스타일 제거
import { dasboardLayoutList } from '@/api/dashboard' // API 호출 제거
import { useNavigatorStore } from '@/store/navigator'
const navigatorStore = useNavigatorStore()
import { useUserStore } from '@/store/user'
const userStore = useUserStore()
const uiStore = useUiStore()

// props 정의
const props = defineProps({
  name: String,
  employeeIdOptions: {
    type: Array,
    default: () => []
  },
  fiscalYearOptions: {
    type: Array,
    default: () => []
  },
  gridArea: {
    type: Object,
    default: () => ({})
  },
  clientRect: {
    type: Object,
    default: () => ({
      top: 0,
      left: 0,
      width: 0,
      height: 0,
    })
  },
  data: {
    type: Object,
    default: () => ({
      data: {}
    })
  },

})

// 로컬 ref 선언 및 초기화
const widget = ref(null)
const isFullscreen = ref(false)
const fullscreenSwiperInstance = ref(null)
const fullscreenSwiperContainer = ref(null)
const chart = ref(null)
const layout = reactive([]) // reactive 제거
// const localSelectedChartType = ref('');
// const localSelectedFiscalYear = ref('');
// const localSelectedLegend = ref('');
// const localSelectedEmployeeId = ref('');
const localSelectedChartType = ref('');
const localSelectedFiscalYear = ref('');
const localSelectedLegend = ref('');
const localSelectedEmployeeId = ref('');



const selectedChartType = ref('')
const selectedEmployeeId = ref(userStore.isAdmin ? '' : userStore.user.employeeId)
const selectedLegend = ref('')
const selectedFiscalYear = ref(String(common.getThisYear()))


const allLayoutData = inject('allLayoutData', ref([]));

// 템플릿에서 반복적으로 사용되는 범례 옵션
const SELECT_OPTIONS_LEGEND = [
  { text: '영업 대표', value: 'chief' },
  { text: '영업 기회', value: 'opportunity' },
  { text: 'End User', value: 'enduser' },
  { text: 'Stage', value: 'stage' },
  { text: '상품', value: 'goods' },
  { text: '매입처', value: 'purchaser',  },
  { text: '전체', value: 'all' },
  { text: 'Q1', value: '1'},
  { text: 'Q2', value: '2' },
  { text: 'Q3', value: '3' },
  { text: 'Q4', value: '4' }
];

// computed 속성 정의
const getChartComponent = computed(() => {
  const charts = {
    'FCST Summary': FCSTSummary,
    'Pipeline Management': PipelineManagement,
    'Final Balance FCST': FinalBalanceFCST,
    'Pipeline Hygiene': PipelineHygiene,
    'Total Rev. FCST': TotalRevFCST,
    'Total GM FCST': TotalGMFCST,
    'Rev. FCST': RevFCST,
    'GM FCST': GMFCST,
    'Monthly Rev. & GM FCST': MonthlyRevGMFCST,
    'Pipeline Status': PipelineStatus,
    'Total Rev. & GM FCST Weekly History': WeeklyHistory,
  }
  return charts[props.name]
})

const selectChartType = computed(() => {
  const whitelist = [
    {
      name: 'Total Rev. FCST',
      options: ['doughnut', 'vertical', 'horizontal'],
      default: 'doughnut'
    },
    {
      name: 'Total GM FCST',
      options: ['doughnut', 'vertical', 'horizontal'],
      default: 'doughnut'
    },
    {
      name: 'Rev. FCST',
      options: ['doughnut', 'vertical', 'horizontal'],
      default: 'doughnut'
    },
    {
      name: 'GM FCST',
      options: ['doughnut', 'vertical', 'horizontal'],
      default: 'doughnut'
    },
    {
      name: 'Monthly Rev. & GM FCST',
      options: ['vertical', 'horizontal'],
      default: 'vertical'
    },
    {
      name: 'Total Rev. & GM FCST Weekly History',
      options: ['line', 'bar'],
      default: 'line'
    },
  ]
  const chart = whitelist.find(item => item.name === props.name)
  return {
    isShow: whitelist.map(item => item.name).includes(props.name),
    options: chart?.options || [],
    default: chart?.default || ''
  }
})

function getItemChartTypeInfo(itemName) {
  const whitelist = [ {
    name: 'Total Rev. FCST',
    options: ['doughnut', 'vertical', 'horizontal'],
    default: 'doughnut'
  },
  {
    name: 'Total GM FCST',
    options: ['doughnut', 'vertical', 'horizontal'],
    default: 'doughnut'
  },
  {
    name: 'Rev. FCST',
    options: ['doughnut', 'vertical', 'horizontal'],
    default: 'doughnut'
  },
  {
    name: 'GM FCST',
    options: ['doughnut', 'vertical', 'horizontal'],
    default: 'doughnut'
  },
  {
    name: 'Monthly Rev. & GM FCST',
    options: ['vertical', 'horizontal'],
    default: 'vertical'
  },
  {
    name: 'Total Rev. & GM FCST Weekly History',
    options: ['line', 'bar'],
    default: 'line'
  }, ];
  const chartConfig = whitelist.find(item => item.name === itemName);
  return {
    isShow: !!chartConfig,
    options: chartConfig?.options || [],
    default: chartConfig?.default || ''
  };
}

const displayEmployeeId = computed(() => {
  return props.name !== 'Final Balance FCST'
})

const displayEmployeeId2 = computed(() => {
  const list = [
    {
      name: 'Pipeline Status'
    },
    {
      name: 'FCST Summary'
    },
    {
      name: 'Pipeline Hygiene'
    },
    {
      name: 'Pipeline Management'
    }
    ,
    {
      name: 'Total Rev. & GM FCST Weekly History'
    }
  ]
  return list.some(item => item.name === props.name)
})
function shouldDisplayEmployeeId(itemName) {
  const listForDisplay = [ {
    name: 'Pipeline Status'
  },
  {
    name: 'FCST Summary'
  },
  {
    name: 'Pipeline Hygiene'
  },
  {
    name: 'Pipeline Management'
  }
  ,
  {
    name: 'Total Rev. & GM FCST Weekly History'
  } ];
  const isExcluded = itemName === 'Final Balance FCST';
  const isInList = listForDisplay.some(item => item.name === itemName);

  if (isExcluded) return false;
  return true;
}

function shouldDisplayEmployeeIdGroup1(itemName) {
  const listForDisplay = [ {
    name: 'Pipeline Status'
  },
  {
    name: 'FCST Summary'
  },
  {
    name: 'Pipeline Hygiene'
  },
  {
    name: 'Pipeline Management'
  }
  ,
  {
    name: 'Total Rev. & GM FCST Weekly History'
  } ];
  const isExcluded = itemName === 'Final Balance FCST';
  return !isExcluded && listForDisplay.some(item => item.name === itemName);
}
function shouldDisplayEmployeeIdGroup2(itemName) {
  const listForDisplay = [ {
    name: 'Pipeline Status'
  },
  {
    name: 'FCST Summary'
  },
  {
    name: 'Pipeline Hygiene'
  },
  {
    name: 'Pipeline Management'
  }
  ,
  {
    name: 'Total Rev. & GM FCST Weekly History'
  } ];
  const isExcluded = itemName === 'Final Balance FCST';
  return !isExcluded && !listForDisplay.some(item => item.name === itemName);
}
const displayFiscalYear = computed(() => {
  const list = [
    {
      name: 'Total Rev. FCST'
    },
    {
      name: 'Total GM FCST'
    },
    {
      name: 'Rev. FCST'
    },
    {
      name: 'GM FCST'
    },
    {
      name: 'Pipeline Status'
    },
    {
      name: 'FCST Summary'
    },
    {
      name: 'Pipeline Hygiene'
    },
    {
      name: 'Pipeline Management'
    },
    {
      name: 'Monthly Rev. & GM FCST'
    }
  ]
  return list.some(item => item.name === props.name)
})

function shouldDisplayFiscalYear(itemName) {
  const list = [ {
    name: 'Total Rev. FCST'
  },
  {
    name: 'Total GM FCST'
  },
  {
    name: 'Rev. FCST'
  },
  {
    name: 'GM FCST'
  },
  {
    name: 'Pipeline Status'
  },
  {
    name: 'FCST Summary'
  },
  {
    name: 'Pipeline Hygiene'
  },
  {
    name: 'Pipeline Management'
  },
  {
    name: 'Monthly Rev. & GM FCST'
  } ];
  return list.some(item => item.name === itemName);
}

const selectLegend = computed(() => {
  const whitelist = [
    {
      name: 'Total Rev. FCST',
      options: ['chief', 'stage', 'goods']
    },
    {
      name: 'Total GM FCST',
      options: ['chief', 'stage', 'goods']
    },
    {
      name: 'Rev. FCST',
      options: ['stage', 'goods']
    },
    {
      name: 'GM FCST',
      options: ['stage', 'goods']
    },
    {
      name: 'Monthly Rev. & GM FCST',
      options: ['enduser', 'goods', 'purchaser']
    },
    {
      name: 'Total Rev. & GM FCST Weekly History',
      options: ['all']
    },
  ]
  const SELECT_OPTIONS = [
    {
      text: '영업 대표',
      value: 'chief',
    },
    {
      text: '영업 기회',
      value: 'opportunity',
    },
    {
      text: 'End User',
      value: 'enduser',
    },
    {
      text: 'Stage',
      value: 'stage',
    },
    {
      text: '상품',
      value: 'goods',
    },
    {
      text: '매입처',
      value: 'purchaser',
    },
    {
      text: '전체',
      value: 'all'
    },
    {
      text: 'Q1',
      value: '1'
    },
    {
      text: 'Q2',
      value: '2'
    },
    {
      text: 'Q3',
      value: '3'
    },
    {
      text: 'Q4',
      value: '4'
    }
  ];

  const SELECT_OPTIONS_LEGEND = [ ...SELECT_OPTIONS ];
  function getOptions() {
    const options = whitelist.find(item => item.name === props.name)?.options
    if (options) {
      if(!selectedLegend.value)selectedLegend.value = options.at(0)
      if (props.name === 'Total Rev. & GM FCST Weekly History' && chart.value?.quarter) {
        chart.value.quarter.forEach(num => {
          options.push(num)
        })
      }
      return options.map(name => {
        return SELECT_OPTIONS.find(item => item.value === name)
      })
    }
    return []
  }
  const options3 = whitelist.find(item => item.name === props.name)?.options || [];
  return {
    isShow: whitelist.map(item => item.name).includes(props.name),
    options: getOptions(),
    default: options3.at(0)
  }
})

function getItemLegendInfo(itemName) {
  const whitelist = [ {
    name: 'Total Rev. FCST',
    options: ['chief', 'stage', 'goods']
  },
  {
    name: 'Total GM FCST',
    options: ['chief', 'stage', 'goods']
  },
  {
    name: 'Rev. FCST',
    options: ['stage', 'goods']
  },
  {
    name: 'GM FCST',
    options: ['stage', 'goods']
  },
  {
    name: 'Monthly Rev. & GM FCST',
    options: ['enduser', 'goods', 'purchaser']
  },
  {
    name: 'Total Rev. & GM FCST Weekly History',
    options: ['all']
  }, ];
  const legendConfig = whitelist.find(item => item.name === itemName);
  const options = legendConfig?.options || [];
  const mappedOptions = options.map(name => SELECT_OPTIONS_LEGEND.find(item => item.value === name)).filter(Boolean);

  return {
    isShow: !!legendConfig,
    options: mappedOptions,
    default: options.length > 0 ? options[0] : ''
  };
}

const resolveChartComponent = (itemName)=> {
  const charts = {
    'FCST Summary': FCSTSummary,
    'Pipeline Management': PipelineManagement,
    'Final Balance FCST': FinalBalanceFCST,
    'Pipeline Hygiene': PipelineHygiene,
    'Total Rev. FCST': TotalRevFCST,
    'Total GM FCST': TotalGMFCST,
    'Rev. FCST': RevFCST,
    'GM FCST': GMFCST,
    'Monthly Rev. & GM FCST': MonthlyRevGMFCST,
    'Pipeline Status': PipelineStatus,
    'Total Rev. & GM FCST Weekly History': WeeklyHistory,
  }
  return charts[itemName] || null;
}

const linkReport = computed(() => {
  const links = {
    'FCST Summary': "/rd/MRD_RT_001",
    'Pipeline Management': "/rd/MRD_RT_001",
    'Final Balance FCST': "/rd/MRD_RT_002",
    'Pipeline Hygiene': "/rd/MRD_RT_004",
    'Total Rev. FCST': "/rd/MRD_RT_001",
    'Total GM FCST': "/rd/MRD_RT_001",
    'Rev. FCST': "/rd/MRD_RT_001",
    'GM FCST': "/rd/MRD_RT_001",
    'Monthly Rev. & GM FCST': "/rd/MRD_RT_003",
    'Pipeline Status': "/rd/MRD_RT_001",
  }
  return links[props.name]
})

function getLinkReportPath(itemName) {
  const links = { 'FCST Summary': "/rd/MRD_RT_001",
  'Pipeline Management': "/rd/MRD_RT_001",
  'Final Balance FCST': "/rd/MRD_RT_002",
  'Pipeline Hygiene': "/rd/MRD_RT_004",
  'Total Rev. FCST': "/rd/MRD_RT_001",
  'Total GM FCST': "/rd/MRD_RT_001",
  'Rev. FCST': "/rd/MRD_RT_001",
  'GM FCST': "/rd/MRD_RT_001",
  'Monthly Rev. & GM FCST': "/rd/MRD_RT_003",
  'Pipeline Status': "/rd/MRD_RT_001", };
  return links[itemName];
}

const fullscreenWidth = computed(() => {
  return window.innerWidth / 100 * 50 || 0
})



defineExpose({
  selectedEmployeeId,
  name: props.name,
  element: widget,
  selectedFiscalYear
})

onBeforeRouteLeave((to, from, next) => {
  if (isFullscreen.value == true) {
    displayFullscreen()
  }
  next()
});

// emit
const emit = defineEmits(['captureWidget', 'move', 'resize'])

// methods
function getStyle() {
  if (Object.values(props.clientRect).every(item => !item)) {
    return {
      position: 'relative',
      height: 'calc(100vh - 300px)'
    }
  }
  const { top, left, width, height } = props.clientRect
  return {
    width: `${width}px`,
    height: `${height}px`,
    transform: `translate3d(${left}px, ${top}px, 0px)`
  }
}

async function captureWidget() {
  const dataUrl = widget.value.querySelector('canvas').toDataURL("image/png")
  emit('captureWidget', props.name, {
    img: `<img src='${dataUrl}' />`,
    employeeId: props.employeeIdOptions.find(item => item.value === localSelectedEmployeeId.value)?.text || '',
    legend: selectLegend.value.options.find(item => item.value === localSelectedLegend.value)?.text || '',
  })
}

function displayFullscreen() {
  const currentState = {
        localSelectedChartType: localSelectedChartType.value,
        localSelectedFiscalYear: localSelectedFiscalYear.value,
        localSelectedLegend: localSelectedLegend.value,
        localSelectedEmployeeId: localSelectedEmployeeId.value,
    };
  uiStore.enterFullscreen(allLayoutData, props.name, currentState);
}

const destroyFullscreenSwiper = () => {
  if (fullscreenSwiperInstance.value) {
    fullscreenSwiperInstance.value.destroy(true, true);
    fullscreenSwiperInstance.value = null;
  }
};
const initFullscreenSwiper = () => {
  destroyFullscreenSwiper();
  if (fullscreenSwiperContainer.value) {
    fullscreenSwiperInstance.value = new Swiper(fullscreenSwiperContainer.value, {
      modules: [Navigation],
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      observer: true,
      observeParents: true,
    });

  }
};
watch(()=>isFullscreen, (newVal, oldVal) => {
  if (newVal === true) {
    nextTick(() => {
      initFullscreenSwiper();
    });
  } else if (newVal === false && oldVal === true) {
    destroyFullscreenSwiper();
  }
});

// lifecycle
// onBeforeMount(async()=>{
//   try {
//     const result = await dasboardLayoutList();
//     const mappedResult = result.rtn
//       .filter(item => item.use_yn === 'y')
//       .map(item => ({ 
//         name: item.widgets_nm,
//         cd: item.widgets_cd,
//         top: parseInt(item.widgets_top),
//         left:parseInt(item.widgets_left),
//         width: parseInt(item.widgets_width),
//         height: parseInt(item.widgets_height),
//       }));

//     // 각 item에 상태 및 옵션 정보 추가
//     const processedLayout = mappedResult.map(item => {
//       const chartTypeInfo = getItemChartTypeInfo(item.name);
//       const legendInfo = getItemLegendInfo(item.name);

//       const chartState = reactive({
//         localSelectedChartType: chartTypeInfo.default,
//         localSelectedFiscalYear: String(common.getThisYear()),
//         localSelectedEmployeeId: userStore.isAdmin ? '' : userStore.user.employeeId,
//         localSelectedLegend: legendInfo.default,
//       });

//       return {
//         ...item,
//         chartState,
//         // 옵션 정보 (필요 시)
//         chartTypeOptions: chartTypeInfo.options,
//         legendOptions: legendInfo.options,
//         // 표시 여부 플래그
//         displayFiscalYear: shouldDisplayFiscalYear(item.name),
//         displayEmployeeIdGroup1: shouldDisplayEmployeeIdGroup1(item.name),
//         displayEmployeeIdGroup2: shouldDisplayEmployeeIdGroup2(item.name),
//         showChartTypeSelector: chartTypeInfo.isShow,
//         showLegendSelector: legendInfo.isShow,
//         // 링크 리포트 경로
//         linkReport: getLinkReportPath(item.name)
//       };
//     });

//     // layout 반응형 배열 업데이트
//     layout.splice(0, layout.length, ...processedLayout);
//   } catch (error) {

//   }
// })

onMounted(async() => {
    const result = await dasboardLayoutList();
    const mappedResult = result.rtn
      .filter(item => item.use_yn === 'y')
      .map(item => ({ 
        name: item.widgets_nm,
        cd: item.widgets_cd,
        top: parseInt(item.widgets_top),
        left:parseInt(item.widgets_left),
        width: parseInt(item.widgets_width),
        height: parseInt(item.widgets_height),
      }));

    // 각 item에 상태 및 옵션 정보 추가
    const processedLayout = mappedResult.map(item => {
      const chartTypeInfo = getItemChartTypeInfo(item.name);
      const legendInfo = getItemLegendInfo(item.name);

      const chartState = reactive({
        localSelectedChartType: chartTypeInfo.default,
        localSelectedFiscalYear: String(common.getThisYear()),
        localSelectedEmployeeId: userStore.isAdmin ? '' : userStore.user.employeeId,
        localSelectedLegend: legendInfo.default,
      });

      return {
        ...item,
        chartState,
        // 옵션 정보 (필요 시)
        chartTypeOptions: chartTypeInfo.options,
        legendOptions: legendInfo.options,
        // 표시 여부 플래그
        displayFiscalYear: shouldDisplayFiscalYear(item.name),
        displayEmployeeIdGroup1: shouldDisplayEmployeeIdGroup1(item.name),
        displayEmployeeIdGroup2: shouldDisplayEmployeeIdGroup2(item.name),
        showChartTypeSelector: chartTypeInfo.isShow,
        showLegendSelector: legendInfo.isShow,
        // 링크 리포트 경로
        linkReport: getLinkReportPath(item.name)
      };
    });

    // layout 반응형 배열 업데이트
    layout.splice(0, layout.length, ...processedLayout);

  new Swiper('.swiper', {
    // Optional parameters
    modules: [Navigation],
    direction: 'horizontal',
    loop: false,

    // Navigation arrows
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },

  });

  // layout이 변경될 때 props.name에 해당하는 item을 찾아 localSelected 상태를 설정합니다.
  const currentItem = layout.find(item => item.name === props.name);
  if (currentItem) {
    localSelectedChartType.value = currentItem.chartState.localSelectedChartType || '';
    localSelectedFiscalYear.value = currentItem.chartState.localSelectedFiscalYear || String(common.getThisYear());
    localSelectedLegend.value = currentItem.chartState.localSelectedLegend || '';
    localSelectedEmployeeId.value = currentItem.chartState.localSelectedEmployeeId || (userStore.isAdmin ? '' : userStore.user.employeeId);
  } else {
    // props.name에 해당하는 item이 layout에 없을 경우, 기본값으로 설정합니다.
    localSelectedChartType.value =  selectChartType.value.default || '';
    localSelectedFiscalYear.value =  String(common.getThisYear());
    localSelectedLegend.value =  selectLegend.value.default || '';
    localSelectedEmployeeId.value =  userStore.isAdmin ? '' : userStore.user.employeeId;
  }

  uiStore.updateWidgetState(props.name, {
    localSelectedChartType: localSelectedChartType.value,
    localSelectedFiscalYear: localSelectedFiscalYear.value,
    localSelectedLegend: localSelectedLegend.value,
    localSelectedEmployeeId: localSelectedEmployeeId.value,
  });

});

watch(()=>uiStore.isGlobalFullscreen, (newVal) => {
  let widgetState;
  if (!newVal && uiStore.fullscreenWidgetName === props.name) {
    widgetState = uiStore.widgetStates[props.name];
    
    if (widgetState) {
      localSelectedChartType.value = widgetState.localSelectedChartType || selectChartType.value.default || '';
      localSelectedFiscalYear.value = widgetState.localSelectedFiscalYear ||  String(common.getThisYear());
      localSelectedLegend.value = widgetState.localSelectedLegend || '';
      localSelectedEmployeeId.value = widgetState.localSelectedEmployeeId || (userStore.isAdmin ? '' : userStore.user.employeeId);
    }
  } else if (newVal && uiStore.fullscreenWidgetName === props.name) {
    widgetState = uiStore.widgetStates[props.name];
    if(widgetState){
      layout.forEach(item=> {
        if(item.name === props.name){
          item.chartState.localSelectedChartType = localSelectedChartType.value;
          item.chartState.localSelectedFiscalYear = localSelectedFiscalYear.value;
          item.chartState.localSelectedLegend = localSelectedLegend.value;
          item.chartState.localSelectedEmployeeId = localSelectedEmployeeId.value;
        }
      })
    }
  }
});

</script>

<style scoped>
.widget {
  position: absolute;
  display: flex;
  flex-direction: column;
  row-gap: .5rem;
  user-select: none;
  transition: all .2s;
  z-index: 1;
  border: 1px solid #b7c1db;
  border-radius: 0.5rem;
  background-color: #fff;
  overflow: hidden;
}

.widget .title {
  font-weight: 700;
  text-align: center;
}

.title2 {
  font-weight: 700;
  text-align: center;
  font-size: 4rem;
}

.widget.dragging {
  transition: none;
  z-index: 1000;
}

.widget.resizing {
  transition: none;
  opacity: .5;
  z-index: 1000;
}

.btn-wrap {
  display: flex;
}

.widget-bottom {
  display: flex;
  margin: 20px;
  margin-top: auto;
}

.widget-bottom .link-report {
  margin-left: auto;
  margin-top: -7px;
}
.widget-bottom .link-report2 {
  margin-left: auto;
}

.btn-resize {
  position: absolute;
  width: 20px;
  height: 20px;
  bottom: 0;
  right: 0;
  background: url(data:image/svg+xml;base64,PHN2ZyBzdHlsZT0iYmFja2dyb3VuZC1jb2xvcjojZmZmZmZmMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgd2lkdGg9IjYiIGhlaWdodD0iNiI+PHBhdGggZD0iTTYgNkgwVjQuMmg0LjJWMEg2djZ6IiBvcGFjaXR5PSIuMzAyIi8+PC9zdmc+);
  background-position: 100% 100%;
  padding: 0 3px 3px 0;
  background-repeat: no-repeat;
  background-origin: content-box;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  cursor: se-resize;
  z-index: 1;
}

.radio-wrap {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.options {
  display: flex;
  justify-content: space-between;
  padding: 0 1rem;
}
.options2 {
  display: flex;
  justify-content: space-between;
  padding: 0 1rem;
  margin-top : -7px;
}
.options3 {
  display: flex;
  justify-content: space-between;
  width: var(--fullscreenWidth);
  padding: 7px 1rem;
}

#fullscreen-widget {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2rem;
  background-color: #fff;
  z-index: 9999;
}

#fullscreen-widget .btn-wrap {
  align-items:center;
  margin-left: auto;
}

.fullscreen-content {
  display: flex;
  justify-content: center;
  align-items: center;
width: 70vw;
  height: 70vh;
}

.radio-btn-toggle {
  background-color: #fff;
}

.btn-check:checked+.btn {
  background: none;
  background-color: #e5eaef;
}

.move {
  cursor: move;
}

.fa-chart-simple.horizontal {
  rotate: 90deg;
}

</style>
